import React, { createContext, useContext, useState, useCallback } from 'react';

const AutoPilotContext = createContext();
export const STORAGE_KEY = 'ff_autopilot_v1';

// ═══════════════════════════════════════════════════════════════════════════
// AUTOPILOT PRESENTATION SCRIPT
// Credentials and routes sourced from DemoStepContext.js (ground truth).
// postLoginTarget is written to localStorage by the login handler so
// the overlay can force-navigate to the intended page after the auth
// redirect — regardless of which role the auth system defaults to.
// ═══════════════════════════════════════════════════════════════════════════
export const AUTOPILOT_SCRIPT = [

  // ── Step 0: Kick off — navigate from homepage to Flowbot ─────────────
  {
    id: 0, page: '/',
    phase: 'Introduction',
    phaseColor: '#4B5563',
    annotation: 'Starting the Forwardsflow automated demo — Flowbot-centric T-bill investment, bank competition, and platform administration.',
    narratorNote: 'Watch the Flowbot pipeline from investor request to bank settlement.',
    duration: 1500,
    action: 'navigate',
    target: '/products?tab=investors',
  },

  // ══════════════════════════════════════════════════════════════════════════
  // PHASE 1: Investor Journey — Flowbot T-Bill Investment
  // ══════════════════════════════════════════════════════════════════════════
  {
    id: 1, page: '/products',
    phase: 'Phase 1 — Investor Journey',
    phaseColor: '#4B5563',
    annotation: 'Flowbot Investor Experience — an investor opens Flowbot and requests a T-bill investment. Multiple banks compete to offer the best rate.',
    narratorNote: 'Mobile-first T-bill investment via WhatsApp, Telegram, or WebChat.',
    duration: 4000,
    action: 'wait',
  },
  {
    id: 2, page: '/products',
    phase: 'Phase 1 — Investor Journey',
    phaseColor: '#4B5563',
    annotation: 'The investor enters their desired investment amount (KES 50,000), maturity duration (30 days), and rollover preference. Flowbot broadcasts the request to all partner banks.',
    narratorNote: 'No app download needed. Investor controls the terms.',
    duration: 4000,
    action: 'wait',
  },
  {
    id: 3, page: '/products',
    phase: 'Phase 1 — Investor Journey',
    phaseColor: '#4B5563',
    annotation: '3 competing quotes received: Equity Bank (3.2%), KCB Bank (3.5%), Coop Bank (3.8%). Investor accepts the best yield from Equity Bank.',
    narratorNote: 'Genuine bank competition drives yields up. Transparent pricing in action.',
    duration: 5000,
    action: 'wait',
  },
  {
    id: 4, page: '/products',
    phase: 'Phase 1 — Investor Journey',
    phaseColor: '#4B5563',
    annotation: 'Investment confirmed. KES 50,000 settled via M-PESA. The entire process took under 5 minutes.',
    narratorNote: 'From request to settlement in minutes, not days.',
    duration: 4000,
    action: 'wait',
  },
  {
    id: 5, page: '/products',
    phase: 'Phase 1 — Investor Journey',
    phaseColor: '#4B5563',
    annotation: 'Navigating to the Flowbot interactive page to see the full investor chat experience...',
    narratorNote: '',
    duration: 2000,
    action: 'navigate',
    target: '/flowbot',
  },
  {
    id: 6, page: '/flowbot',
    phase: 'Phase 1 — Investor Journey',
    phaseColor: '#4B5563',
    annotation: 'Flowbot interactive chat — investors interact via a familiar messaging interface. Banks compete behind the scenes. Every interaction builds transaction data.',
    narratorNote: 'Each Flowbot session accumulates transaction history, building a path to financial resilience.',
    duration: 6000,
    action: 'wait',
  },

  // ══════════════════════════════════════════════════════════════════════════
  // PHASE 2: Bank Dashboard — Bank Admin views pipeline and portfolio
  // ══════════════════════════════════════════════════════════════════════════
  {
    id: 7, page: '/flowbot',
    phase: 'Phase 2 — Bank Dashboard',
    phaseColor: '#4B5563',
    annotation: 'Switching to Bank perspective — logging in as NCBA Bank Kenya to see the investor pipeline and T-bill portfolio.',
    narratorNote: 'Banks see pre-qualified, KYC-approved investors without any acquisition cost.',
    duration: 3000,
    action: 'navigate',
    target: '/login',
  },
  {
    id: 8, page: '/login',
    phase: 'Phase 2 — Bank Dashboard',
    phaseColor: '#4B5563',
    annotation: 'Logging in as Margaret Waweru — Bank Admin, NCBA Bank Kenya...',
    narratorNote: '',
    duration: 1500,
    action: 'fill-email',
    value: 'admin@ncba.com',
  },
  {
    id: 9, page: '/login',
    phase: 'Phase 2 — Bank Dashboard',
    phaseColor: '#4B5563',
    annotation: 'Entering credentials...',
    narratorNote: '',
    duration: 1000,
    action: 'fill-password',
    value: 'Demo123!',
  },
  {
    id: 10, page: '/login',
    phase: 'Phase 2 — Bank Dashboard',
    phaseColor: '#4B5563',
    annotation: 'Signing in as Bank Admin...',
    narratorNote: '',
    duration: 800,
    action: 'login',
    email: 'admin@ncba.com',
    password: 'Demo123!',
    nextPage: '/bank/admin',
  },
  {
    id: 11, page: '/bank/admin',
    phase: 'Phase 2 — Bank Dashboard',
    phaseColor: '#4B5563',
    annotation: 'Bank Admin Dashboard — NCBA Bank Kenya overview: investor pipeline, active forwards, risk scoring, and treasury analytics.',
    narratorNote: 'Pre-qualified investors arrive via Flowbot. The bank focuses on treasury and forwards operations, not customer acquisition.',
    duration: 5000,
    action: 'wait',
  },
  {
    id: 12, page: '/bank/admin',
    phase: 'Phase 2 — Bank Dashboard',
    phaseColor: '#4B5563',
    annotation: 'Navigating to treasury dashboard to view active forwards and compliance status...',
    narratorNote: '',
    duration: 2000,
    action: 'navigate',
    target: '/bank/treasury',
  },
  {
    id: 13, page: '/bank/treasury',
    phase: 'Phase 2 — Bank Dashboard',
    phaseColor: '#4B5563',
    annotation: 'Treasury Dashboard — active forwards originated through Flowbot, with automated compliance checks and settlement tracking.',
    narratorNote: 'Automated compliance checks streamline operations. Settlement tracking runs automatically.',
    duration: 5000,
    action: 'wait',
  },
  {
    id: 14, page: '/bank/treasury',
    phase: 'Phase 2 — Bank Dashboard',
    phaseColor: '#4B5563',
    annotation: 'Viewing risk scoring dashboard — credit scores, investor profiles, and portfolio quality metrics...',
    narratorNote: '',
    duration: 2000,
    action: 'navigate',
    target: '/credit-scoring',
  },
  {
    id: 15, page: '/credit-scoring',
    phase: 'Phase 2 — Bank Dashboard',
    phaseColor: '#4B5563',
    annotation: 'Credit Scoring Dashboard — automated risk assessment across all counterparties. Data-driven creditworthiness built from every interaction.',
    narratorNote: 'Every Flowbot conversation builds transaction history. Better data means better treasury and compliance decisions.',
    duration: 6000,
    action: 'wait',
  },

  // ══════════════════════════════════════════════════════════════════════════
  // PHASE 3: Platform Admin — Forwardsflow network overview
  // ══════════════════════════════════════════════════════════════════════════
  {
    id: 16, page: '/credit-scoring',
    phase: 'Phase 3 — Platform Administration',
    phaseColor: '#374151',
    annotation: 'Switching to Forwardsflow Platform Admin — network origination metrics, multi-bank comparison, and platform P&L.',
    narratorNote: 'The admin sees the full network: all banks, all investors, all origination channels.',
    duration: 3000,
    action: 'navigate',
    target: '/login',
  },
  {
    id: 17, page: '/login',
    phase: 'Phase 3 — Platform Administration',
    phaseColor: '#374151',
    annotation: 'Logging in as Forwardsflow Platform Administrator...',
    narratorNote: '',
    duration: 1500,
    action: 'fill-email',
    value: 'admin@forwardsflow.com',
  },
  {
    id: 18, page: '/login',
    phase: 'Phase 3 — Platform Administration',
    phaseColor: '#374151',
    annotation: 'Entering credentials...',
    narratorNote: '',
    duration: 1000,
    action: 'fill-password',
    value: 'Admin123!',
  },
  {
    id: 19, page: '/login',
    phase: 'Phase 3 — Platform Administration',
    phaseColor: '#374151',
    annotation: 'Signing in as Platform Admin...',
    narratorNote: '',
    duration: 800,
    action: 'login',
    email: 'admin@forwardsflow.com',
    password: 'Admin123!',
    nextPage: '/admin',
  },
  {
    id: 20, page: '/admin',
    phase: 'Phase 3 — Platform Administration',
    phaseColor: '#374151',
    annotation: 'Forwardsflow Admin Dashboard — network-wide origination metrics, multi-bank performance comparison, and platform fee revenue.',
    narratorNote: 'Fee-based revenue model: 1% origination, 0.25% foreign exchange/forwards/arrangement/redemption.',
    duration: 5000,
    action: 'wait',
  },
  {
    id: 21, page: '/admin',
    phase: 'Phase 3 — Platform Administration',
    phaseColor: '#374151',
    annotation: 'Navigating to platform P&L — fee-based revenue across all partner banks and origination channels.',
    narratorNote: '',
    duration: 2000,
    action: 'navigate',
    target: '/admin/pnl',
  },
  {
    id: 22, page: '/admin/pnl',
    phase: 'Phase 3 — Platform Administration',
    phaseColor: '#374151',
    annotation: 'Platform P&L — fee revenue from T-bill origination, foreign exchange commissions, forwards brokerage, and arrangement fees across the network.',
    narratorNote: 'Forwardsflow earns fees on infrastructure — no principal risk, no balance sheet exposure.',
    duration: 6000,
    action: 'wait',
  },

  // ══════════════════════════════════════════════════════════════════════════
  // PHASE 4: Wholesale Funding Marketplace — Investor Demo
  // ══════════════════════════════════════════════════════════════════════════
  {
    id: 23, page: '/admin/pnl',
    phase: 'Phase 4 — Wholesale Funding Marketplace',
    phaseColor: '#1F2937',
    annotation: 'Switching to the Wholesale Funding Marketplace — logging in as an investor analyst to explore foreign-exchange-protected frontier-market exposure.',
    narratorNote: 'Investors access the same risk infrastructure proven by Flowbot Phase I.',
    duration: 3000,
    action: 'navigate',
    target: '/login',
  },
  {
    id: 24, page: '/login',
    phase: 'Phase 4 — Wholesale Funding Marketplace',
    phaseColor: '#1F2937',
    annotation: 'Logging in as Sarah Chen — Investor Analyst, Horizon Impact Fund...',
    narratorNote: '',
    duration: 1500,
    action: 'fill-email',
    value: 'analyst@horizonimpact.com',
  },
  {
    id: 25, page: '/login',
    phase: 'Phase 4 — Wholesale Funding Marketplace',
    phaseColor: '#1F2937',
    annotation: 'Entering credentials...',
    narratorNote: '',
    duration: 1000,
    action: 'fill-password',
    value: 'Demo123!',
  },
  {
    id: 26, page: '/login',
    phase: 'Phase 4 — Wholesale Funding Marketplace',
    phaseColor: '#1F2937',
    annotation: 'Signing in as Investor Analyst...',
    narratorNote: '',
    duration: 800,
    action: 'login',
    email: 'analyst@horizonimpact.com',
    password: 'Demo123!',
    nextPage: '/investor/analyst',
  },
  {
    id: 27, page: '/investor/analyst',
    phase: 'Phase 4 — Wholesale Funding Marketplace',
    phaseColor: '#1F2937',
    annotation: 'Investor Analyst Dashboard — wholesale funding operations overview with foreign-exchange-protected portfolio positions, impact metrics, and real-time analytics.',
    narratorNote: 'Full visibility into underlying bank positions with institutional-grade transparency.',
    duration: 5000,
    action: 'wait',
  },
  {
    id: 28, page: '/investor/analyst',
    phase: 'Phase 4 — Wholesale Funding Marketplace',
    phaseColor: '#1F2937',
    annotation: 'Navigating to the wholesale funding opportunities marketplace...',
    narratorNote: '',
    duration: 2000,
    action: 'navigate',
    target: '/investor/opportunities',
  },
  {
    id: 29, page: '/investor/opportunities',
    phase: 'Phase 4 — Wholesale Funding Marketplace',
    phaseColor: '#1F2937',
    annotation: 'Wholesale Funding Opportunities — browse foreign-exchange-protected deposit instruments from Flowbot partner banks across Kenya, Ghana, South Africa, Botswana, and Zambia.',
    narratorNote: 'Each opportunity is compliance-verified with built-in KYC/AML documentation.',
    duration: 5000,
    action: 'wait',
  },
  {
    id: 30, page: '/investor/opportunities',
    phase: 'Phase 4 — Wholesale Funding Marketplace',
    phaseColor: '#1F2937',
    annotation: 'Navigating to credit scoring for due diligence on underlying bank positions...',
    narratorNote: '',
    duration: 2000,
    action: 'navigate',
    target: '/credit-scoring',
  },
  {
    id: 31, page: '/credit-scoring',
    phase: 'Phase 4 — Wholesale Funding Marketplace',
    phaseColor: '#1F2937',
    annotation: 'Credit Scoring — investors can perform due diligence on the same risk data that powers Flowbot treasury operations.',
    narratorNote: 'Transparent risk: full visibility into credit quality of counterparties.',
    duration: 4000,
    action: 'wait',
  },
  {
    id: 32, page: '/credit-scoring',
    phase: 'Phase 4 — Wholesale Funding Marketplace',
    phaseColor: '#1F2937',
    annotation: 'Switching to Investor Admin to execute a capital call subscription...',
    narratorNote: '',
    duration: 2000,
    action: 'navigate',
    target: '/login',
  },
  {
    id: 33, page: '/login',
    phase: 'Phase 4 — Wholesale Funding Marketplace',
    phaseColor: '#1F2937',
    annotation: 'Logging in as James Okafor — Investor Admin, Horizon Impact Fund...',
    narratorNote: '',
    duration: 1500,
    action: 'fill-email',
    value: 'admin@horizonimpact.com',
  },
  {
    id: 34, page: '/login',
    phase: 'Phase 4 — Wholesale Funding Marketplace',
    phaseColor: '#1F2937',
    annotation: 'Entering credentials...',
    narratorNote: '',
    duration: 1000,
    action: 'fill-password',
    value: 'Demo123!',
  },
  {
    id: 35, page: '/login',
    phase: 'Phase 4 — Wholesale Funding Marketplace',
    phaseColor: '#1F2937',
    annotation: 'Signing in as Investor Admin...',
    narratorNote: '',
    duration: 800,
    action: 'login',
    email: 'admin@horizonimpact.com',
    password: 'Demo123!',
    nextPage: '/investor/admin',
  },
  {
    id: 36, page: '/investor/admin',
    phase: 'Phase 4 — Wholesale Funding Marketplace',
    phaseColor: '#1F2937',
    annotation: 'Investor Admin Dashboard — wholesale funding operations with capital call management, foreign exchange hedging controls, and organization-wide portfolio analytics.',
    narratorNote: 'Admin users manage subscriptions, foreign exchange hedging, and compliance across the fund.',
    duration: 5000,
    action: 'wait',
  },
  {
    id: 37, page: '/investor/admin',
    phase: 'Phase 4 — Wholesale Funding Marketplace',
    phaseColor: '#1F2937',
    annotation: 'Navigating to foreign exchange hedge page — programmatic forwards protection on every position...',
    narratorNote: '',
    duration: 2000,
    action: 'navigate',
    target: '/investor/fx-hedge',
  },
  {
    id: 38, page: '/investor/fx-hedge',
    phase: 'Phase 4 — Wholesale Funding Marketplace',
    phaseColor: '#1F2937',
    annotation: 'Foreign Exchange Hedge Page — programmatic forwards contracts eliminate the frontier currency penalty. Every position is foreign-exchange-protected automatically.',
    narratorNote: 'Exchange rate protected returns: the yield of frontier forwards with the foreign exchange profile of developed markets.',
    duration: 5000,
    action: 'wait',
  },
  {
    id: 39, page: '/investor/fx-hedge',
    phase: 'Phase 4 — Wholesale Funding Marketplace',
    phaseColor: '#1F2937',
    annotation: 'Navigating to portfolio view — track foreign-exchange-protected wholesale funding positions...',
    narratorNote: '',
    duration: 2000,
    action: 'navigate',
    target: '/investor/investments',
  },
  {
    id: 40, page: '/investor/investments',
    phase: 'Phase 4 — Wholesale Funding Marketplace',
    phaseColor: '#1F2937',
    annotation: 'Portfolio — active foreign-exchange-protected wholesale funding positions with maturity tracking, yield monitoring, and impact metrics.',
    narratorNote: 'Complete audit-ready documentation for every position.',
    duration: 5000,
    action: 'wait',
  },
  {
    id: 41, page: '/investor/investments',
    phase: 'Phase 4 — Wholesale Funding Marketplace',
    phaseColor: '#1F2937',
    annotation: 'Navigating to the secondary market — trade existing positions with other investors...',
    narratorNote: '',
    duration: 2000,
    action: 'navigate',
    target: '/investor/secondary-market',
  },
  {
    id: 42, page: '/investor/secondary-market',
    phase: 'Phase 4 — Wholesale Funding Marketplace',
    phaseColor: '#1F2937',
    annotation: 'Secondary Market — investors can list positions for sale and bid on positions from other investors, providing liquidity to the wholesale funding marketplace.',
    narratorNote: 'Secondary market adds liquidity and divestment flexibility to frontier-market positions.',
    duration: 5000,
    action: 'wait',
  },

  // ── Done ─────────────────────────────────────────────────────────────────
  {
    id: 43, page: '/investor/secondary-market',
    phase: 'Demo Complete',
    phaseColor: '#4B5563',
    annotation: '✓ Demo complete. 4 phases: Investor Journey → Bank Dashboard → Platform Admin → Wholesale Funding Marketplace. Forwardsflow: programmatic risk infrastructure for frontier economies.',
    narratorNote: 'Invite questions. Click Restart to watch again or explore the platform manually.',
    duration: 99999,
    action: 'done',
  },
];

// ═══════════════════════════════════════════════════════════════════════════
// CONTEXT
// ═══════════════════════════════════════════════════════════════════════════

export const loadAutoPilotState = () => {
  try {
    const s = localStorage.getItem(STORAGE_KEY);
    return s ? JSON.parse(s) : null;
  } catch { return null; }
};

export const saveAutoPilotState = (state) => {
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(state)); } catch {}
};

export const AutoPilotProvider = ({ children }) => {
  const saved = loadAutoPilotState();
  const [active, setActive] = useState(saved?.active || false);
  const [paused, setPaused] = useState(saved?.paused || false);
  const [stepId, setStepId] = useState(saved?.stepId ?? 0);

  const start = useCallback(() => {
    const state = { active: true, paused: false, stepId: 0 };
    saveAutoPilotState(state);
    setActive(true);
    setPaused(false);
    setStepId(0);
    window.location.href = '/';
  }, []);

  const pause = useCallback(() => {
    const s = loadAutoPilotState();
    saveAutoPilotState({ ...s, paused: true });
    setPaused(true);
  }, []);

  const resume = useCallback(() => {
    const s = loadAutoPilotState();
    saveAutoPilotState({ ...s, paused: false });
    setPaused(false);
  }, []);

  const stop = useCallback(() => {
    localStorage.removeItem(STORAGE_KEY);
    setActive(false);
    setPaused(false);
    setStepId(0);
  }, []);

  const advanceTo = useCallback((newStepId) => {
    const s = loadAutoPilotState();
    saveAutoPilotState({ ...s, stepId: newStepId });
    setStepId(newStepId);
  }, []);

  return (
    <AutoPilotContext.Provider value={{ active, paused, stepId, start, pause, resume, stop, advanceTo }}>
      {children}
    </AutoPilotContext.Provider>
  );
};

export const useAutoPilot = () => {
  const ctx = useContext(AutoPilotContext);
  if (!ctx) {
    return { active: false, step: 0, running: false, start: () => {}, stop: () => {}, reset: () => {} };
  }
  return ctx;
};
export default AutoPilotContext;
